<script>
    import { createForm } from "svelte-forms-lib";
    import { onDestroy } from "svelte";
    import { getSettingsDTO, setSettings, settingsFormSchema } from "../../../services/settingService";
    import toast from "svelte-french-toast";
    import { closeModal, openModal } from "svelte-modals";
    import FormCard from "../FormCard.svelte";
    import InputField from "../InputField.svelte";
    import { settings } from "../../../stores/settingsStore";
    import RadioButtons from "../../SearchBars/RadioButtons.svelte";
    import TextareaField from "../TextareaField.svelte";
    import AlertModal from "../../Modal/AlertModal.svelte";
    import ImageField from "./ImageField.svelte";

    let isPressed = false;
    const disableSubscriptionsOptions = [
        { label: "SI", value: "true", id: "disableSubscriptionsYes" },
        { label: "NO", value: "false", id: "disableSubscriptionsNo" },
    ];

    const { form, isValid, handleSubmit, handleChange, errors } = createForm({
        initialValues: {
            subscriptionActiveYear: "",
            registrationMinAge: "",
            ugsMaxAgeInclusive: "",
            minimumPaymentUGS: "",
            minimumPaymentNotUGS: "",
            maximumPayment: "",
            disableSubscriptions: "",
            disableSubscriptionsLabel: "",
            membershipCardFront: undefined,
            membershipCardBack: undefined,
        },
        validationSchema: settingsFormSchema,
        onSubmit: async (form) => {
            if (form.maximumPayment < form.minimumPaymentUGS) {
                $errors.minimumPaymentUGS = "Inserisci una cifra minore del pagamento massimo";
                return;
            }

            if (form.maximumPayment < form.minimumPaymentNotUGS) {
                $errors.minimumPaymentNotUGS = "Inserisci una cifra minore del pagamento massimo";
                return;
            }

            if (form.ugsMaxAgeInclusive <  form.registrationMinAge) {
                $errors.ugsMaxAgeInclusive = "Inserisci un'età maggiore o uguale di quella minima";
                return;
            }

            openModal(AlertModal, {
                title: `Modifica impostazioni globali`,
                message: "Sei sicuro di voler modificare le impostazioni globali?",
                containerClass: "w-10/12 md:w-4/12",
                onConfirm: () => {
                    editSettings(getSettingsDTO(form));
                    closeModal();
                }
            });
        }
    });

    async function editSettings(newSettings) {
        try {
            isPressed = true;
            const res = await setSettings(newSettings);

            if (res?.status === 200) {
                toast.success("Impostazioni modificate");
                $settings = res.data;

                return;
            }
            toast.error("Non è stato possibile modificare le impostazioni");
        } catch (err) {
            if(err?.response?.status === 500) {
                toast.error(`Impossibile modificare le impostazioni, errore ${err.toString()}`);
            }
        } finally {
            isPressed = false;
        }
    }

    const unsubscribeSettings = settings.subscribe((data) => $form = {
        subscriptionActiveYear: data.subscriptionActiveYear,
        registrationMinAge: data.registrationMinAge,
        ugsMaxAgeInclusive: data.ugsMaxAgeInclusive,
        minimumPaymentUGS: data.minimumPaymentUGS,
        minimumPaymentNotUGS: data.minimumPaymentNotUGS,
        maximumPayment: data.maximumPayment,
        disableSubscriptionsLabel: data.disableSubscriptionsLabel,
        membershipCardFront: data.membershipCardFront,
        membershipCardBack: data.membershipCardBack,
        disableSubscriptions: data?.disableSubscriptions.toString() ?? "false"
    });

    onDestroy(unsubscribeSettings);
</script>

<FormCard
    fullTextSubject="Impostazioni globali"
    isEdit={false}
    isButtonDisabled={isPressed || !$isValid}
    handleSubmit={handleSubmit}
>

    <InputField
        fieldName="Anno d'iscrizione"
        type="number"
        placeholder="Anno attuale d'iscrizione"
        name="subscriptionActiveYear"
        isRequired={false}
        handleChange={handleChange}
        bind:errorText={$errors.subscriptionActiveYear}
        bind:value={$form.subscriptionActiveYear}
    />

    <div class="w-full flex flex-col gap-3 md:flex-row">
        <InputField
            fieldName="Limite età per l'iscrizione"
            type="number"
            placeholder="Limite età per l'iscrizione"
            name="registrationMinAge"
            isRequired={false}
            handleChange={handleChange}
            bind:errorText={$errors.registrationMinAge}
            bind:value={$form.registrationMinAge}
        />

        <InputField
            fieldName="Limite età per UGS"
            type="number"
            placeholder="Età massima per la partecipazione all'UGS"
            name="ugsMaxAgeInclusive"
            isRequired={false}
            handleChange={handleChange}
            bind:errorText={$errors.ugsMaxAgeInclusive}
            bind:value={$form.ugsMaxAgeInclusive}
        />
    </div>

    <div class="w-full flex flex-col gap-3 mb-2 md:flex-row">
        <div class="w-full md:w-1/2 relative">
            <span class="euro-icon">€</span>

            <InputField
                fieldName="Pagamento minimo UGS"
                type="number"
                placeholder="Pagamento minimo UGS in euro"
                isRequired={false}
                name="minimumPaymentUGS"
                handleChange={handleChange}
                bind:errorText={$errors.minimumPaymentUGS}
                bind:value={$form.minimumPaymentUGS}
            />
        </div>

        <div class="w-full md:w-1/2 relative">
            <span class="euro-icon">€</span>

            <InputField
                fieldName="Pagamento minimo non UGS"
                type="number"
                placeholder="Pagamento minimo non UGS in euro"
                isRequired={false}
                name="minimumPaymentNotUGS"
                handleChange={handleChange}
                bind:errorText={$errors.minimumPaymentNotUGS}
                bind:value={$form.minimumPaymentNotUGS}
            />
        </div>
    </div>

    <div class="w-full relative">
        <span class="euro-icon">€</span>

        <InputField
            fieldName="Pagamento massimo"
            min="10"
            type="number"
            placeholder="Pagamento massimo in euro"
            name="maximumPayment"
            isRequired={false}
            handleChange={handleChange}
            bind:errorText={$errors.maximumPayment}
            bind:value={$form.maximumPayment}
        />
    </div>

    <div class="w-full flex flex-col gap-3 mb-2 md:flex-row">
        <div class="w-full md:w-full h-64">
            <ImageField
                fieldName="Fronte della tessera"
                bind:image="{$form.membershipCardFront}"
            />
        </div>

        <div class="w-full md:w-full h-64">
            <ImageField
                fieldName="Retro della tessera"
                bind:image="{$form.membershipCardBack}"
            />
        </div>
    </div>

    <div>
        <div class="font-450 text-lg">Le iscrizioni sono sospese?</div>

        <RadioButtons
            isForm={true}
            options={disableSubscriptionsOptions}
            isNullable={false}
            bind:inputValue={$form.disableSubscriptions}
        />
    </div>

    <TextareaField
        fieldName="Messaggio iscrizioni disabilitate"
        placeholder="Le iscrizioni sono momentaneamente sospese"
        name="disableSubscriptionsLabel"
        isRequired={false}
        handleChange={handleChange}
        bind:errorText={$errors.disableSubscriptionsLabel}
        bind:value={$form.disableSubscriptionsLabel}
    />

</FormCard>

<style>
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }

    .euro-icon {
        position: absolute;
        top: 2.07rem;
        right: 35px;
    }
</style>